import { api } from './api';

// Memory-only: no private text or translation responses are persisted in the browser.
const cache = new Map();
const queue = new Map();
const failed = new Map();
const listeners = new Set();
let timer;
let active = 0;
let revision = 0;
export const subscribeTranslations = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const translationSnapshot = () => revision;
const notify = () => { revision += 1; listeners.forEach(fn => fn()); };
const keyFor = (lang, text) => `${lang}:${text}`;

export function contentTranslation(text, lang) {
  if (typeof text !== 'string' || !text.trim()) return text;
  if (lang === 'en' && !/[\u0900-\u097f\u0b00-\u0b7f]/.test(text)) return text;
  const source = text.trim();
  const key = keyFor(lang, source);
  if (cache.has(key)) return cache.get(key);
  if (!queue.has(key) && !failed.has(key)) {
    queue.set(key, { lang, text: source });
    clearTimeout(timer);
    timer = setTimeout(flush, 60);
  }
  return text;
}

async function flush() {
  if (active || !queue.size) return;
  const lang = queue.values().next().value.lang;
  let size = 0;
  const batch = [...queue.values()].filter(item => item.lang === lang).filter(item => {
    size += item.text.length;
    return size <= 18000;
  }).slice(0, 30);
  if (!batch.length) return;
  active += 1;
  notify();
  try {
    const { data } = await api.post('/translate/batch', { target: lang, texts: batch.map(item => item.text) }, { timeout: 90000 });
    for (const item of batch) {
      const translated = data.translations[item.text];
      if (translated) cache.set(keyFor(lang, item.text), translated);
      else failed.set(keyFor(lang, item.text), item);
    }
  } catch {
    batch.forEach(item => failed.set(keyFor(lang, item.text), item));
  } finally {
    batch.forEach(item => queue.delete(keyFor(lang, item.text)));
    active -= 1;
    notify();
    if (queue.size) timer = setTimeout(flush, 0);
  }
}

export function translationStatus(lang) {
  return {
    loading: [...queue.values()].some(item => item.lang === lang),
    error: [...failed.values()].some(item => item.lang === lang),
  };
}

export function retryTranslations(lang) {
  for (const [key, item] of failed) {
    if (item.lang === lang) { failed.delete(key); queue.set(key, item); }
  }
  notify();
  timer = setTimeout(flush, 0);
}
